import { Injectable, signal } from '@angular/core';
import { Address } from '@eadsysteme/ewsrealestateclient-v2';
import { AddressTableEntity } from '../../app/components/real-estates-legacy/models/real-estate-table.model';

export interface RecentRealEstate {
	id: string;
	area: number;
	number: number;
	address?: AddressTableEntity;
	lastOpened?: string;
}

@Injectable({
	providedIn: 'root',
})
export class RecentRealEstateCacheService {
	public readonly recentRealEstates = signal<RecentRealEstate[]>([]);

	private static readonly RECENT_MAX = 20;

	/** No trailing underscore on purpose, so the prefix filter for area keys never picks it up. */
	private static readonly USER_KEY = 'recentRealEstateUser';

	/** Key vor der Umbenennung. Wird nur gelesen, damit die Historie nicht einmalig verworfen wird. */
	private static readonly LEGACY_USER_KEY = 'recentRealEstateOwner';

	private static recentKey(area: number): string {
		return `recentRealEstates_${area}`;
	}

	static toAddressTableEntity(addr?: Address): AddressTableEntity {
		if (!addr) return undefined;
		return {
			street: [addr.streets?.[0]?.streetName, addr.streets?.[0]?.houseNumber].filter(Boolean).join(' '),
			zipCode: addr.zipCode,
			city: addr.city,
		};
	}

	addRecent(re: RecentRealEstate): void {
		if (!re?.id || !re?.area || re?.number == null) return;
		const key = RecentRealEstateCacheService.recentKey(re.area);

		let existing: RecentRealEstate[];
		try {
			existing = JSON.parse(localStorage.getItem(key) ?? '[]');
		} catch {
			existing = [];
		}

		const enriched: RecentRealEstate = { ...re, lastOpened: new Date().toISOString() };

		const last = existing.at(-1);
		if (last?.id === re.id && existing.length <= RecentRealEstateCacheService.RECENT_MAX) {
			const updated = [...existing.slice(0, -1), { ...last, lastOpened: enriched.lastOpened, address: re.address ?? last.address }];
			localStorage.setItem(key, JSON.stringify(updated));
		} else {
			const filtered = existing.filter((r) => r.id !== re.id);
			const updated = [...filtered, enriched].slice(-RecentRealEstateCacheService.RECENT_MAX);
			localStorage.setItem(key, JSON.stringify(updated));
		}

		RecentRealEstateCacheService.removeDuplicateFromOtherAreas(re.id, re.area);
		this.refreshSignal();
	}

	/** Reads all area caches and merges into one list sorted by lastOpened (newest last). */
	loadRecent(): void {
		this.refreshSignal();
	}

	clearRecent(area: number): void {
		localStorage.removeItem(RecentRealEstateCacheService.recentKey(area));
		this.refreshSignal();
	}

	/** Drops the history when it was written by a different portal user, tracked by the Keycloak user id. */
	syncUser(userId: string | undefined | null): void {
		if (!userId) {
			return;
		}
		const storedUserId = localStorage.getItem(RecentRealEstateCacheService.USER_KEY) ?? localStorage.getItem(RecentRealEstateCacheService.LEGACY_USER_KEY);
		if (storedUserId !== userId) {
			this.clearAll();
		}
		localStorage.setItem(RecentRealEstateCacheService.USER_KEY, userId);
		localStorage.removeItem(RecentRealEstateCacheService.LEGACY_USER_KEY);
	}

	/** Clears every area cache, e.g. after redeeming a code, since localStorage is not bound to a user. */
	clearAll(): void {
		const keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i)).filter((key) => key?.startsWith('recentRealEstates_'));
		for (const key of keys) {
			localStorage.removeItem(key);
		}
		this.refreshSignal();
	}

	static findAreaByRealEstateId(id: string): number | null {
		if (!id) return null;
		let bestArea: number | null = null;
		let bestDate = '';
		try {
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i);
				if (!key?.startsWith('recentRealEstates_')) continue;
				const area = Number(key.replace('recentRealEstates_', ''));
				if (!Number.isFinite(area)) continue;
				const items: RecentRealEstate[] = JSON.parse(localStorage.getItem(key) ?? '[]');
				const match = items.find((re) => re.id === id);
				if (match) {
					const opened = match.lastOpened ?? '';
					if (!bestArea || opened > bestDate) {
						bestArea = area;
						bestDate = opened;
					}
				}
			}
		} catch {
			// ignore parse errors
		}
		return bestArea;
	}

	private refreshSignal(): void {
		const all: RecentRealEstate[] = [];
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (!key?.startsWith('recentRealEstates_')) continue;
			try {
				const items: RecentRealEstate[] = JSON.parse(localStorage.getItem(key) ?? '[]');
				all.push(...items);
			} catch {
				// skip this area key on parse error
			}
		}
		all.sort((a, b) => (a.lastOpened ?? '').localeCompare(b.lastOpened ?? ''));
		this.recentRealEstates.set(all.slice(-RecentRealEstateCacheService.RECENT_MAX));
	}

	private static removeDuplicateFromOtherAreas(id: string, correctArea: number): void {
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (!key?.startsWith('recentRealEstates_')) continue;
			const area = Number(key.replace('recentRealEstates_', ''));
			if (area === correctArea || !Number.isFinite(area)) continue;
			try {
				const items: RecentRealEstate[] = JSON.parse(localStorage.getItem(key) ?? '[]');
				const filtered = items.filter((re) => re.id !== id);
				if (filtered.length !== items.length) {
					localStorage.setItem(key, JSON.stringify(filtered));
				}
			} catch {
				// ignore parse errors
			}
		}
	}
}
